import { format, subDays } from 'date-fns';
import { CardStats, ChartConfig } from "./Types";

const generateLabels = (intervalDays: number, numLabels: number) => {
  const today = new Date();
  const labels = [];
  for (let i = 0; i < numLabels; i++) {
    const date = subDays(today, intervalDays * i);
    labels.push(format(date, 'dd MMM'));
  }
  return labels.reverse(); // Reverse the array to have the oldest date first
};

const generateRandomData = (range: [number, number], numPoints: number): number[] => {
  const [min, max] = range;
  const data = [];
  for (let i = 0; i < numPoints; i++) {
    const randomValue = Math.floor(Math.random() * (max - min + 1)) + min;
    data.push(randomValue);
  }
  return data;
};

const generateRandomDates = (numReviews) => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < numReviews; i++) {
    const randomDaysAgo = Math.floor(Math.random() * 100); // Random number of days ago
    const date = subDays(today, randomDaysAgo);
    dates.push(date);
  }
  return dates;
};

export const cardData: CardStats[] = [
  { cardTitle: 'Users', icon: 'People', statCount: 1200, backgroundColor: '#ff9c9c', cardBorderColor: '#d14646' },
  { cardTitle: 'Downloads', icon: 'Download', statCount: '3k', backgroundColor: '#62fdae', cardBorderColor: '#75d146' },
  { cardTitle: 'Notifications', icon: 'Notifications', statCount: 25, backgroundColor: '#fde676', cardBorderColor: '#e1b63e' },
  { cardTitle: 'Reviews', icon: 'Review', statCount: 354, backgroundColor: '#a3def5', cardBorderColor: '#3eb3e1' }
];

export const chartDataObjects: ChartConfig[] = [
  {
    title: "Active Users",
    chartData: {
      labels: generateLabels(6, 4),
      datasets: [
        {
          label: "Active Users",
          data: generateRandomData([50, 600], 4),
          fill: false,
          borderColor: "#f34c6b",
          tension: 0.1
        }
      ]
    },
    borderColor: "#f34c6b",
    backgroundColor: "rgba(243, 76, 107, 0.2)",
    height: "300px"
  },
  {
    title: "Notifications",
    chartData: {
      labels: generateLabels(1, 15),
      datasets: [
        {
          label: "Notifications",
          data: generateRandomData([4, 18], 15),
          fill: false,
          borderColor: "blue",
          tension: 0.1
        }
      ]
    },
    borderColor: "blue",
    backgroundColor: "rgba(0, 0, 255, 0.2)",
    height: "300px"
  }
];

const androidReviewDates = generateRandomDates(15);
const iOSReviewDates = generateRandomDates(15);

export const androidReviews = [
  {
    authorName: 'John Doe',
    starRating: 5,
    detailedReview: 'This app is fantastic! It has everything I need and is very user-friendly. Highly recommend to everyone!',
    datePosted: androidReviewDates[0]
  },
  {
    authorName: 'Jane Smith',
    starRating: 4,
    detailedReview: 'Great app with lots of features. Sometimes it lags a bit, but overall a very solid experience.',
    datePosted: androidReviewDates[1]
  },
  {
    authorName: 'Alice Johnson',
    starRating: 3,
    detailedReview: 'The app is okay, but it crashes occasionally on my device. Hope the developers can fix this soon.',
    datePosted: androidReviewDates[2]
  },
  {
    authorName: 'Bob Brown',
    starRating: 2,
    detailedReview: 'Not very impressed with the app. It lacks some important features and the UI could be better.',
    datePosted: androidReviewDates[3]
  },
  {
    authorName: 'Charlie Davis',
    starRating: 1,
    detailedReview: 'Terrible experience. The app crashes all the time and is very slow. Would not recommend.',
    datePosted: androidReviewDates[4]
  },
  {
    authorName: 'Maxwell Reed',
    starRating: 4,
    detailedReview: 'Pretty good app with useful features. It could be more intuitive, but it gets the job done.',
    datePosted: androidReviewDates[5]
  },
  {
    authorName: 'Olivia Turner',
    starRating: 5,
    detailedReview: 'I\'m in love with this app! It\'s sleek, fast, and has never given me any trouble.',
    datePosted: androidReviewDates[6]
  },
  {
    authorName: 'Ethan Woods',
    starRating: 1,
    detailedReview: 'The app is full of bugs. It\'s frustrating to use and keeps freezing.',
    datePosted: androidReviewDates[7]
  },
  {
    authorName: 'Sophia Carter',
    starRating: 3,
    detailedReview: 'It\'s an okay app. Not the best I\'ve used, but not the worst either.',
    datePosted: androidReviewDates[8]
  },
  {
    authorName: 'Liam Johnson',
    starRating: 2,
    detailedReview: 'The app has potential, but it needs serious work on performance issues.',
    datePosted: androidReviewDates[9]
  }
];

export const iOSReviews = [
  {
    authorName: 'Emily White',
    starRating: 5,
    detailedReview: 'Absolutely love this app! It has made my life so much easier. The interface is clean and intuitive.',
    datePosted: iOSReviewDates[0]
  },
  {
    authorName: 'Michael Green',
    starRating: 4,
    detailedReview: 'Great app with excellent features. There is room for improvement, but overall very happy with it.',
    datePosted: iOSReviewDates[1]
  },
  {
    authorName: 'Sarah Blue',
    starRating: 5,
    detailedReview: 'This app is a game changer! It works flawlessly and I use it every day.',
    datePosted: iOSReviewDates[2]
  },
  {
    authorName: 'Daniel Black',
    starRating: 3,
    detailedReview: 'The app is decent, but it could use some more features and a bit of polishing.',
    datePosted: iOSReviewDates[3]
  },
  {
    authorName: 'Samantha Brown',
    starRating: 2,
    detailedReview: 'I find the app quite buggy and it crashes frequently on my iPhone. Needs a lot of improvement.',
    datePosted: iOSReviewDates[4]
  },
  {
    authorName: 'David King',
    starRating: 1,
    detailedReview: 'Very disappointing. The app does not deliver what it promises and is very slow.',
    datePosted: iOSReviewDates[5]
  },
  {
    authorName: 'Jessica Moore',
    starRating: 4,
    detailedReview: 'Good app with a lot of potential. It has some bugs, but I am sure they will be fixed in future updates.',
    datePosted: iOSReviewDates[6]
  },
  {
    authorName: 'Brian Adams',
    starRating: 5,
    detailedReview: 'Excellent app! Everything works smoothly and the features are exactly what I needed.',
    datePosted: iOSReviewDates[7]
  },
  {
    authorName: 'Laura Scott',
    starRating: 3,
    detailedReview: 'It\'s an average app. Does the job but nothing extraordinary. The design could be better.',
    datePosted: iOSReviewDates[8]
  },
  {
    authorName: 'Kevin Johnson',
    starRating: 2,
    detailedReview: 'I am not very satisfied with the app. It has too many ads and the performance is not great.',
    datePosted: iOSReviewDates[9]
  },
  {
    authorName: 'Amelia Wilson',
    starRating: 4,
    detailedReview: 'Good functionality and design. A few minor bugs, but nothing too serious.',
    datePosted: iOSReviewDates[10]
  },
  {
    authorName: 'Noah Roberts',
    starRating: 5,
    detailedReview: 'Fantastic app! It\'s become essential for my daily routine.',
    datePosted: iOSReviewDates[11]
  },
  {
    authorName: 'Isabella Brown',
    starRating: 2,
    detailedReview: 'The app is slow and unresponsive at times. Not what I expected.',
    datePosted: iOSReviewDates[12]
  },
  {
    authorName: 'Mason Miller',
    starRating: 3,
    detailedReview: 'Decent app with room for improvement. Looking forward to updates.',
    datePosted: iOSReviewDates[13]
  },
  {
    authorName: 'Ella Davis',
    starRating: 1,
    detailedReview: 'The app crashes constantly. Very poor experience.',
    datePosted: iOSReviewDates[14]
  }
];
